import React, { useState, useEffect } from 'react';
import { PortfolioData } from '../types/portfolio';

interface ImpactProps {
  data: PortfolioData;
}

const Impact: React.FC<ImpactProps> = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('impact-section');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return (
    <section id="impact-section" className="py-24 bg-gradient-to-b from-ivory to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-manrope font-bold text-navy mb-4">
            {data.sections.impact.heading}
          </h2>
          <div className="h-1 w-16 bg-teal mx-auto rounded-full"></div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.sections.impact.metrics.map((metric, index) => (
            <div
              key={index}
              className={`bg-white p-8 rounded-2xl border border-gray-100 hover:border-teal hover:shadow-lg transition-all duration-500 ${
                isVisible ? 'animate-fadeInUp' : 'opacity-0'
              }`}
              style={{
                animationDelay: isVisible ? `${index * 0.1}s` : '0s',
              }}
            >
              <div className="mb-4">
                <div className="inline-block">
                  <span className="text-5xl font-manrope font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal to-gold">
                    {metric.value}
                  </span>
                </div>
              </div>
              <p className="text-slate text-base leading-relaxed">
                {metric.label}
              </p>
              <div className="mt-6 h-1 w-8 bg-gradient-to-r from-teal to-gold rounded-full"></div>
            </div>
          ))}
        </div>

        {/* Supporting Text */}
        <div className="mt-16 p-8 bg-white rounded-2xl border border-gray-100 text-center">
          <p className="text-slate text-lg leading-relaxed">
            These metrics reflect documented outcomes from business analysis initiatives, focusing on operational efficiency, data visibility, and quality improvements across multiple engagement areas.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Impact;
